/* Reusable outcome table component used by the Run Detail page.
   Iter 39 Phase 3.

   Renders the same column set as the Affected Companies table in the
   launcher (LogoEnrichment.jsx) but pulls from the per-run outcomes
   endpoint (GET /api/data/admin/logos/enrich/{run_id}/outcomes). The
   container is responsible for the data fetch + filter state; this
   component only paints rows. */

import { Link } from "react-router-dom";
import { ArrowSquareOut } from "@phosphor-icons/react";
import {
    ConfidenceChip,
    ScoreCell,
    OutcomeChip,
    LogoCandidatePreview,
    WouldWriteCell,
} from "./enrichmentOutcomeChips";

export default function OutcomeTable({ outcomes = [], emptyLabel = "No outcomes match the current filters." }) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-slate-500">
                    <tr>
                        <th className="px-4 py-2.5 font-medium w-14">Preview</th>
                        <th className="px-4 py-2.5 font-medium">Company</th>
                        <th className="px-4 py-2.5 font-medium">Identity</th>
                        <th className="px-4 py-2.5 font-medium">Discovered domain</th>
                        <th className="px-4 py-2.5 font-medium">Acquisition</th>
                        <th className="px-4 py-2.5 font-medium">Confidence</th>
                        <th className="px-4 py-2.5 font-medium" title="V2.0 composite score · hover for breakdown">Score</th>
                        <th className="px-4 py-2.5 font-medium">Would write?</th>
                        <th className="px-4 py-2.5 font-medium">Outcome</th>
                        <th className="px-4 py-2.5 font-medium w-10"></th>
                    </tr>
                </thead>
                <tbody data-testid="outcome-table-rows" className="divide-y divide-slate-100">
                    {outcomes.length === 0 && (
                        <tr>
                            <td colSpan={10} className="px-4 py-8 text-center text-slate-400" data-testid="outcome-table-empty">
                                {emptyLabel}
                            </td>
                        </tr>
                    )}
                    {outcomes.map((r) => {
                        const ident = r.nse_symbol || r.bse_code || r.company_id;
                        return (
                            <tr key={r.company_id} data-testid={`outcome-row-${r.company_id}`} className="hover:bg-slate-50">
                                <td className="px-4 py-2.5">
                                    <LogoCandidatePreview url={r.candidate_logo_url} />
                                </td>
                                <td className="px-4 py-2.5 text-slate-900">{r.company_name || "—"}</td>
                                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{ident}</td>
                                <td className="px-4 py-2.5 font-mono text-xs">
                                    {r.discovered_domain ? (
                                        <>
                                            <a
                                                href={`https://${r.discovered_domain}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-slate-700 hover:text-blue-600 hover:underline"
                                                title={`Open https://${r.discovered_domain} in a new tab`}
                                                data-testid={`outcome-discovered-domain-${r.company_id}`}
                                            >
                                                {r.discovered_domain}
                                            </a>
                                            <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1.5">
                                                <span>via {r.domain_source || "?"}</span>
                                                {r.domain_verified && !r.downgrade_reason && (
                                                    <span className="text-emerald-700">· verified</span>
                                                )}
                                                {r.downgrade_reason && (
                                                    <span
                                                        className="inline-block uppercase tracking-wide px-1 py-px rounded bg-amber-100 text-amber-700 text-[9px] font-semibold"
                                                        title={`Generic-stem downgrade: ${r.downgrade_reason}`}
                                                    >
                                                        downgraded
                                                    </span>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-slate-600">
                                    {r.logo_source ? (
                                        <div className="space-y-0.5">
                                            <div className="font-mono text-slate-700">{r.logo_source}</div>
                                            {r.candidate_logo_url && (
                                                <a
                                                    href={r.candidate_logo_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-mono text-[10px] text-blue-600 hover:underline truncate block max-w-[260px]"
                                                    title={r.candidate_logo_url}
                                                >
                                                    {r.candidate_logo_url}
                                                </a>
                                            )}
                                            {r.logo_size > 0 && (
                                                <div className="text-[10px] text-slate-400 font-mono">
                                                    {r.logo_ext || "?"} · {r.logo_size.toLocaleString()} B
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-slate-300">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5"><ConfidenceChip value={r.confidence} /></td>
                                <td className="px-4 py-2.5">
                                    <ScoreCell
                                        score={r.confidence_score}
                                        breakdown={r.confidence_score_breakdown}
                                        companyId={r.company_id}
                                    />
                                </td>
                                <td className="px-4 py-2.5"><WouldWriteCell row={r} /></td>
                                <td className="px-4 py-2.5"><OutcomeChip row={r} /></td>
                                <td className="px-4 py-2.5">
                                    <Link
                                        to={`/data/companies/${encodeURIComponent(ident)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-slate-400 hover:text-slate-900 inline-flex"
                                        title="Open company detail"
                                        data-testid={`outcome-row-link-${r.company_id}`}
                                    >
                                        <ArrowSquareOut size={14} />
                                    </Link>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
